import { baseApi } from './base'

export enum Category {
  status = 'status',
  type = 'type',
}

type Statistics = {
  chart: StatisticsChart
}

// export type Chart = {
//   datasets: Array<{
//     data: number[]
//     label?: string
//   }>
//   labels: string[]
// }

type ConsultationsStatistics = {
  category?: Partial<Record<Category, Statistics>>
  periodicity?: Partial<Record<Periodicity, Statistics>>
}

type GetConsultationsStatisticsParams = {
  category?: Category[]
  establishment: string
  periodicity?: Periodicity[]
}

type GetConsultationRankingParams = {
  establishment: string
  startDate?: string
  endDate?: string
  groupByDay?: boolean
}

export enum Periodicity {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

type StatisticsChart = {
  datasets: Array<{
    data: number[]
    label?: string
  }>
  labels: string[]
}

export type RankCount = {
  name: string
  surname: string
  email: string
  count: number
}

export type DailyUserConsultationCount = {
  name: string
  surname: string
  email: string
  day: string
  count: number
}

type GetExpertiseRankingParams = {
  establishment: string
  startDate?: string
  endDate?: string
  groupByDay?: boolean
}

export type DailyExpertiseStatusCount = {
  day: string
  status: string
  count: number
}

export type DailyUserExpertiseCount = {
  name: string
  surname: string
  email: string
  day: string
  count: number
}

export interface ConsultationDurationMetrics {
  durationDistribution: {
    range: string
    count: number
    isOutlier: boolean
  }[]

  medianByDay: {
    day: string
    median: number
    p25: number
    p75: number
    type?: string
  }[]

  dataQuality: {
    totalConsultations: number
    validDurations: number
    qualityScore: number
    zombieSessions: number
  }
}

export type AIUsageByDay = {
  day: string
  count: number
}

export type AIUsageByUser = {
  userId: string
  name: string
  surname: string
  email: string
  count: number
}

export interface AIUsageStatistics {
  transcriptions: {
    total: number
    byDay: AIUsageByDay[]
    byUser: AIUsageByUser[]
  }
  reports: {
    total: number
    byDay: AIUsageByDay[]
    byUser: AIUsageByUser[]
  }
}

export type EntityEvolutionByDay = {
  day: string
  count: number
  cumulative: number
}

export interface EntityEvolutionStatistics {
  consultations: EntityEvolutionByDay[]
  expertises: EntityEvolutionByDay[]
  patients: EntityEvolutionByDay[]
}

// Monthly Report types
export type MonthlyReportParams = {
  establishment: string
  startDate?: string
  endDate?: string
}

export type GeneralAttendanceRow = {
  establishmentId: string
  establishmentName: string
  gender: string
  ageBucket: string
  count: number
}

export type TestCountRow = {
  testLabel: string
  count: number
  establishmentId: string
  establishmentName: string
  source: 'mesure' | 'medical-act'
}

export type ThresholdAlerts = {
  highGlycemia: number
  highBloodPressure: number
  highBPOver40: number
  noECGOver40: number
  establishmentId: string
  establishmentName: string
}

export type MaternityAttendanceRow = {
  establishmentId: string
  establishmentName: string
  ageBucket: string
  trimester: string
  count: number
}

export type MissingExamsRow = {
  establishmentId: string
  establishmentName: string
  trimester: string
  missingEcho: number
  missingECG: number
}

export const statisticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConsultationsStatistics: builder.query<{ total: number }, void>({
      query: () => ({ url: '/statistics/consultations', method: 'GET' }),
    }),
    getEstablishmentConsultationsStatistics: builder.query<ConsultationsStatistics, GetConsultationsStatisticsParams>({
      query: ({ establishment, ...params }) => ({
        url: `/statistics/establishments/${establishment}/consultations`,
        method: 'GET',
        params,
      }),
    }),
    getEstablishmentPatientsStatistics: builder.query<Statistics, string>({
      query: (establishmentId) => ({
        url: `/statistics/establishments/${establishmentId}/patients`,
        method: 'GET',
      }),
    }),
    getEstablishmentConsultationRanking: builder.query<RankCount[] | DailyUserConsultationCount[], GetConsultationRankingParams>({
      query: ({ establishment, ...params }) => ({
        url: `/statistics/establishments/${establishment}/consultations/ranking`,
        method: 'POST',
        body: params,
      }),
    }),
    getEstablishmentExpertiseRanking: builder.query<RankCount[] | DailyUserExpertiseCount[], GetExpertiseRankingParams>({
      query: ({ establishment, ...params }) => ({
        url: `/statistics/establishments/${establishment}/expertises/ranking`,
        method: 'POST',
        body: params,
      }),
    }),
    getEstablishmentExpertiseStatusStatistics: builder.query<DailyExpertiseStatusCount[], GetExpertiseRankingParams>({
      query: ({ establishment, ...params }) => ({
        url: `/statistics/establishments/${establishment}/expertises/status`,
        method: 'GET',
        params,
      }),
    }),
    getEstablishmentDurationMetrics: builder.query<
      ConsultationDurationMetrics,
      { establishment: string; startDate?: string; endDate?: string; includeByType?: boolean }
    >({
      query: ({ establishment, ...params }) => {
        const queryParams = {
          ...params,
          includeByType: params.includeByType,
        }
        return {
          url: `/statistics/establishments/${establishment}/duration`,
          method: 'POST',
          body: queryParams,
        }
      },
    }),
    getEstablishmentAIUsageStatistics: builder.query<
      AIUsageStatistics,
      { establishment: string; startDate?: string; endDate?: string }
    >({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/ai-usage`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getEstablishmentEntityEvolution: builder.query<
      EntityEvolutionStatistics,
      { establishment: string; startDate?: string; endDate?: string }
    >({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/entity-evolution`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getReportGeneralAttendance: builder.query<GeneralAttendanceRow[], MonthlyReportParams>({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/monthly-report/general-attendance`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getReportTestsPerformed: builder.query<TestCountRow[], MonthlyReportParams>({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/monthly-report/tests`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getReportThresholdAlerts: builder.query<ThresholdAlerts[], MonthlyReportParams>({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/monthly-report/threshold-alerts`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getReportMaternityAttendance: builder.query<MaternityAttendanceRow[], MonthlyReportParams>({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/monthly-report/maternity-attendance`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
    getReportMissingExams: builder.query<MissingExamsRow[], MonthlyReportParams>({
      query: ({ establishment, startDate, endDate }) => ({
        url: `/statistics/establishments/${establishment}/monthly-report/missing-exams`,
        method: 'GET',
        params: { startDate, endDate },
      }),
    }),
  }),
  overrideExisting: false,
})
